import moment from 'moment'
import { instance as store } from 'app/store'
import { create } from 'app/utils/data'
import { getJoinedChannel, getClientId } from 'app/utils/data/socket'

const initialState = {
  isProcessingWebDownload: false,
  isProcessingDesktopDownload: false,
  kitDownloadError: false,
  kitDownloadCompleted: false
}

function accountChannelTopic() {
  return `kit-jobs:${store.getters['accounts/currentAccount']?.id}`
}

// A kit download completes only in the browser that initiated it, so we match on
// both the kit and the clientId. (npm-package builds are broadcast to every client
// on the account and are handled separately by the passive status subscription.)
function downloadMessageIsForMe(messageData, { kitToken, clientId }) {
  if (!kitToken || !clientId) return false
  return messageData?.kitToken === kitToken && messageData?.clientId === clientId
}

// Triggers a kit .zip download build and resolves with its `downloadUrl`. This is a
// genuine request/response: the build is scoped to this browser's clientId so the
// download only completes where it was asked for, and the channel listeners are torn
// down as soon as this single build resolves.
async function buildKitDownload({ commit }, { kitToken, buildType }) {
  if ('string' !== typeof kitToken) throw new Error('Missing required parameter: kitToken')
  if ('string' !== typeof buildType) throw new Error('Missing required parameter: buildType')

  const clientId = getClientId()
  const workerName = 'kit_download'

  return new Promise(function (resolve, reject) {
    commit('transition', {
      isProcessingWebDownload: buildType === 'web',
      isProcessingDesktopDownload: buildType === 'desktop',
      kitDownloadError: false,
      kitDownloadCompleted: false
    })

    const callbackRefs = []

    const errorCallback = (error, channel) => {
      commit('transition', {
        isProcessingWebDownload: false,
        isProcessingDesktopDownload: false,
        kitDownloadError: true,
        kitDownloadCompleted: false
      })
      if (channel) {
        channel.off(callbackRefs)
      }
      reject('string' === typeof error ? new Error(error) : error)
    }

    getJoinedChannel('/jobs', accountChannelTopic())
      .then((channel) => {
        const refComplete = channel.on(`complete_${workerName}`, (data) => {
          if (!downloadMessageIsForMe(data, { kitToken, clientId })) return

          commit('transition', {
            isProcessingWebDownload: false,
            isProcessingDesktopDownload: false,
            kitDownloadError: false,
            kitDownloadCompleted: true
          })
          channel.off(callbackRefs)
          resolve({ downloadUrl: data.downloadUrl })
        })

        const refTimeout = channel.on(`timeout_${workerName}`, (data) => {
          if (downloadMessageIsForMe(data, { kitToken, clientId })) {
            errorCallback(`timeout_${workerName}`, channel)
          }
        })

        const refFailed = channel.on(`failed_${workerName}`, (data) => {
          if (downloadMessageIsForMe(data, { kitToken, clientId })) {
            errorCallback(`failed_${workerName}`, channel)
          }
        })

        const refError = channel.onError(() => errorCallback('channel error', channel))

        // Track every ref so this single build's listeners can all be removed on
        // resolution, rather than accumulating across repeated download builds.
        callbackRefs.splice(0, -1, refComplete, refTimeout, refError, refFailed)

        channel.push(`build_${workerName}`, { kitToken, buildType, clientId }).receive('error', (e) => errorCallback(`build_${workerName}: ${e}`, channel))
      })
      .catch(errorCallback)
  })
}

// Module-scoped passive subscription to npm-package build status for the whole
// account. Joined once per account session (idempotent) so every kit's status
// stays live regardless of which view is mounted, then torn down on logout /
// account switch via `resetState`. This never triggers a build — it only listens,
// so the status reflects builds started server-side (e.g. as a side effect of an
// icon upload or kit mutation) just as well as ones started here.
let npmStatusSubscription = null

function commitNpmStatus(commit, data, status, lastUpdatedAt = null) {
  if ('string' !== typeof data?.kitToken) return
  commit('setNpmStatus', { token: data.kitToken, status, lastUpdatedAt })
}

// A forced rebuild advances the kit's revision, and completion is the first moment a package
// actually exists at it — so this is where the customer-visible revision may be updated, not at
// the request. Refetching also keeps the change-driven path honest, since those bump the revision
// too.
//
// Scoped to the kit being viewed on purpose: this subscription is account-wide, and `kitFetched`
// assigns `state.item`, so refetching on a background build for a different kit would swap the
// kit out from under the customer.
function refreshKitIfViewing(dispatch, data) {
  const viewing = store.getters['kits/currentKit']

  if (viewing?.token !== data?.kitToken) return

  dispatch('kits/fetchKit', data.kitToken, { root: true }).catch(console.error)
}

async function subscribeToNpmPackageStatus({ commit, dispatch }) {
  const accountId = store.getters['accounts/currentAccount']?.id
  if (!accountId) return
  if (npmStatusSubscription?.accountId === accountId) return

  // Account switch: drop the prior account's listeners before re-joining.
  teardownNpmPackageStatusSubscription()

  const workerName = 'kit_npm_package'
  const channel = await getJoinedChannel('/jobs', `kit-jobs:${accountId}`)

  const refStarted = channel.on(`started_${workerName}`, (data) => commitNpmStatus(commit, data, 'processing'))
  const refComplete = channel.on(`complete_${workerName}`, (data) => {
    commitNpmStatus(commit, data, 'completed', moment.now())
    refreshKitIfViewing(dispatch, data)
  })
  const failureHandler = (data) => commitNpmStatus(commit, data, 'error')
  const refFailed = channel.on(`failed_${workerName}`, failureHandler)
  const refTimeout = channel.on(`timeout_${workerName}`, failureHandler)

  npmStatusSubscription = {
    accountId,
    remove: () => {
      channel.off(`started_${workerName}`, refStarted)
      channel.off(`complete_${workerName}`, refComplete)
      channel.off(`failed_${workerName}`, refFailed)
      channel.off(`timeout_${workerName}`, refTimeout)
    }
  }
}

function teardownNpmPackageStatusSubscription() {
  if (npmStatusSubscription) {
    npmStatusSubscription.remove()
    npmStatusSubscription = null
  }
}

// Kicks off an npm-package rebuild and returns immediately. All status display is
// driven by the passive subscription's broadcasts, so this only optimistically
// marks the kit as publishing and flags an error if the build can't even be
// started (the build itself reports failure/completion over the channel).
async function republishPackage({ commit, dispatch }, kitToken) {
  if ('string' !== typeof kitToken) throw new Error('Missing required parameter: kitToken')

  await dispatch('ensureSubscribed')

  commit('setNpmStatus', { token: kitToken, status: 'processing' })

  const channel = await getJoinedChannel('/jobs', accountChannelTopic())
  channel.push('build_kit_npm_package', { kitToken }).receive('error', () => commit('setNpmStatus', { token: kitToken, status: 'error' }))
}

// Forces a rebuild of a kit's package: republishes CDN assets, rebuilds and re-pushes the npm
// package, and reprovisions — the same recovery the admin tool runs, scoped to one kit.
//
// Unlike `republishPackage` this is an HTTP request, not a channel push. The server sets the
// "force" signal itself, which is why the client sends no body: a force flag the client could
// supply would let anyone skip the rate limit and the revision bump.
//
// The server advances the kit's revision before building, so nothing is published at the new
// revision until the build succeeds. The response therefore carries no revision, and the label
// is only updated from the completion broadcast.
async function rebuildPackage({ commit, dispatch }, kitToken) {
  if ('string' !== typeof kitToken) throw new Error('Missing required parameter: kitToken')

  await dispatch('ensureSubscribed')

  commit('setNpmStatus', { token: kitToken, status: 'processing' })

  try {
    // `data: {}` rather than omitted: the JSON:API serializer reads Object.keys(data). An empty
    // body is the point — the server sets the force signal itself, so there is nothing to send.
    await create({ url: `/api/clouds/kits/${kitToken}/rebuild-package`, type: 'kit-package-rebuild', data: {} })
  } catch (error) {
    // A refusal is not a build failure: nothing was started, so the previous state must not be
    // replaced by an error flash. 429 means "you already had your turn", 409 means "your turn is
    // still happening" — the latter is already reflected by the processing state.
    const refusal = refusalFrom(error)

    if (refusal) {
      commit('setNpmStatus', { token: kitToken, status: refusal.status, retryAfterMs: refusal.retryAfterMs })
    } else {
      commit('setNpmStatus', { token: kitToken, status: 'error' })
    }

    throw error
  }
}

// Axios rejects with an Error carrying `.response`; some layers (and our axios test double)
// reject with the bare response instead. Read either rather than betting on one shape.
function refusalFrom(error) {
  const response = error?.response ?? error
  const status = response?.status

  if (429 !== status && 409 !== status) return null

  const jsonApiError = response?.data?.errors?.[0]

  return {
    status: 429 === status ? 'rate_limited' : 'processing',
    retryAfterMs: jsonApiError?.meta?.['retry-after-ms'] ?? null
  }
}

export default {
  namespaced: true,

  state: {
    ...initialState,
    // token -> { status: 'processing' | 'completed' | 'error' | 'rate_limited', lastUpdatedAt, retryAfterMs }
    npmPackageByToken: {}
  },

  getters: {
    isProcessingWebDownload: (state) => state.isProcessingWebDownload,
    isProcessingDesktopDownload: (state) => state.isProcessingDesktopDownload,
    isProcessingDownload: (state) => state.isProcessingWebDownload || state.isProcessingDesktopDownload,
    kitDownloadCompleted: (state) => state.kitDownloadCompleted,
    kitDownloadError: (state) => state.kitDownloadError,

    // Getter-factories: callers pass the kit token they care about, so any number
    // of kits can report status simultaneously (e.g. every row on /kits).
    npmPackageStatus: (state) => (token) => state.npmPackageByToken[token] ?? null,
    isPublishing: (state) => (token) => state.npmPackageByToken[token]?.status === 'processing'
  },

  mutations: {
    transition(state, transitions) {
      Object.keys(transitions).forEach((key) => {
        state[key] = transitions[key]
      })
    },

    setNpmStatus(state, { token, status, lastUpdatedAt = null, retryAfterMs = null }) {
      state.npmPackageByToken[token] = { status, lastUpdatedAt, retryAfterMs }
    },

    resetState(state) {
      teardownNpmPackageStatusSubscription()
      Object.assign(state, { ...initialState })
      state.npmPackageByToken = {}
    }
  },

  actions: {
    async buildKitForDownload(storeParams, actionParams) {
      return await buildKitDownload(storeParams, actionParams)
    },

    // Republish a kit's npm package (e.g. the "Give it another try?" retry). Most
    // rebuilds happen automatically server-side on kit/icon changes; this is the
    // explicit, customer-initiated trigger.
    async republishPackage(storeParams, kitToken) {
      return await republishPackage(storeParams, kitToken)
    },

    // Customer-initiated forced rebuild from the kit's Package view. Fixes a package that is
    // stale or broken even when the kit itself hasn't changed — the case `republishPackage`
    // cannot handle, because an unchanged kit reuses its previously completed build.
    async rebuildPackage(storeParams, kitToken) {
      return await rebuildPackage(storeParams, kitToken)
    },

    // Join the account's npm-package status broadcast once so the package
    // indicators stay live for builds triggered anywhere (this browser or
    // server-side). Idempotent — safe to dispatch from any kit-aware view's
    // `mounted`; teardown happens on logout / account switch via `resetState`.
    async ensureSubscribed(storeParams) {
      return await subscribeToNpmPackageStatus(storeParams)
    }
  }
}
